/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import { colors, fonts, media, mixins } from '@blend/consumer-components';
import React from 'react';
import { range } from '@utils/lodash';

export interface NPSProps {
  ratingValue: number | null;
  label?: string;
  onChange: (value: number) => void;
}

interface NPSState {
  focusedInput: number | null;
  ratingValue: number | null;
}

const superlativeIds = {
  min: 'NPS-subtextMin',
  max: 'NPS-subtextMax',
};

const choices = range(1, 6);
const getSuperlativeId = (i: number) => {
  switch (i) {
    case Math.min(...choices):
      return superlativeIds.min;
    case Math.max(...choices):
      return superlativeIds.max;
    default:
      return null;
  }
};

const largeMarker = 38;
const mediumMarker = 30;
const smallMarker = 26;
const markerBorderWidth = 2;

const styles = StyleSheet.create({
  survey: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  text: {
    color: colors.steel,
    fontSize: 13,
    fontWeight: fonts.weight.medium,
  },
  choices: {
    ...mixins.margin(12, 0, 6, 0),
    backgroundColor: colors.snowPlus1,
    borderRadius: 500, // pill shape
    display: 'flex',
    justifyContent: 'space-between',
    listStyleType: 'none',
    paddingLeft: 0,
  },
  choiceLabel: {
    fontWeight: fonts.weight.book,
    margin: 0,
    fontSize: 15,
    [media.mobileMedium]: {
      fontSize: 13,
    },
  },
  hiddenInput: {
    opacity: 0,
    position: 'absolute',
  },
  marker: {
    borderRadius: largeMarker,
    cursor: 'pointer',
    display: 'block',
    height: largeMarker,
    lineHeight: `${largeMarker}px`,
    position: 'relative',
    textAlign: 'center',
    transition: 'background-color 0.18s ease-out',
    width: largeMarker,
    [media.mobileMedium]: {
      height: mediumMarker,
      lineHeight: `${mediumMarker}px`,
      width: mediumMarker,
    },
    [media.mobileSmall]: {
      height: smallMarker,
      lineHeight: `${smallMarker}px`,
      width: smallMarker,
    },
  },
  checked: {
    background: colors.white,
    border: `${markerBorderWidth}px solid`,
    boxShadow: mixins.boxShadow(0, 2, 4, 0, mixins.rgba(0, 0, 0, 0.2)),
    height: largeMarker,
    lineHeight: `${largeMarker - 2 * markerBorderWidth}px`,
    width: largeMarker,
    [media.mobileMedium]: {
      lineHeight: `${mediumMarker - 2 * markerBorderWidth}px`,
    },
    [media.mobileSmall]: {
      lineHeight: `${smallMarker - 2 * markerBorderWidth}px`,
    },
  },
  focused: {
    backgroundColor: colors.clayMinus2,
  },
  spectrumLabels: {
    color: colors.clayPlus3,
    display: 'flex',
    fontSize: 13,
    justifyContent: 'space-between',
    width: '100%',
    [media.mobileMedium]: {
      fontSize: 12,
    },
  },
});

class NPS extends React.Component<NPSProps, NPSState> {
  constructor(props: NPSProps) {
    super(props);

    this.state = {
      focusedInput: null,
      ratingValue: null,
    };
  }

  setFocusedInput = (i: number | null) => this.setState({ focusedInput: i });
  handleRatingChange = (ratingValue: number) => {
    const { onChange } = this.props;
    this.setState({ ratingValue });
    onChange(ratingValue);
  };

  render() {
    const { ratingValue } = this.props;

    const { focusedInput } = this.state;

    return (
      <div className={css(styles.survey)} data-it="it-NPS-survey">
        <fieldset>
          <ul className={css(styles.choices)}>
            {choices.map((i: number) => {
              const ratingId = `rating-${i}`;
              const superlativeId = getSuperlativeId(i);
              const otherProps = superlativeId ? { 'aria-describedby': superlativeId } : {};
              const checked = ratingValue === i;
              const focused = focusedInput === i && !checked;
              return (
                <li key={i}>
                  <label
                    className={css(styles.choiceLabel)}
                    data-it="it-NPS-choice"
                    htmlFor={ratingId}
                    onMouseEnter={() => this.setFocusedInput(i)}
                    onMouseLeave={() => this.setFocusedInput(null)}
                    onFocus={() => this.setFocusedInput(i)}
                    onBlur={() => this.setFocusedInput(null)}
                  >
                    <input
                      className={css(styles.hiddenInput)}
                      id={ratingId}
                      name="rating"
                      onClick={() => this.handleRatingChange(i)}
                      type="radio"
                      value={i}
                      {...otherProps}
                    />
                    <div
                      className={css([styles.marker, checked && styles.checked, focused && styles.focused])}
                      data-it={(checked && 'it-NPS-checked-choice') || (focused && 'it-NPS-focused-choice')}
                    >
                      {i}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className={css(styles.spectrumLabels)} aria-hidden="true">
            <span id={superlativeIds.min}>Extremely dissatisfied</span>
            <span id={superlativeIds.max}>Extremely satisfied</span>
          </div>
        </fieldset>
      </div>
    );
  }
}

export default NPS;
